import { useEffect, useMemo, useState } from 'react';
import { preoccupationStatusLabel } from '@isbd/shared';
import { kuunda } from '@/lib/kuunda';
import { useAuth } from '@/features/auth/AuthProvider';
import { AdminEmpty, AdminPageHeader } from '@/features/admin/ui';
import { ListSkeleton } from '@/features/ui/skeleton';
import { useToast } from '@/features/ui/toast/useToast';
import '@/features/admin/admin.css';

interface PreoccRow {
  id: string;
  subject: string;
  body: string;
  status: string;
  priority: string;
  created_at: string;
  sender_id: string;
  sender_email?: string;
}

interface AlertRow {
  id: string;
  body: string;
  kind: string;
  status: string;
  created_at: string;
}

export function AdminAssistancePage() {
  const { profile, isAdmin, isStaff } = useAuth();
  const toast = useToast();
  const [items, setItems] = useState<PreoccRow[]>([]);
  const [replyDraft, setReplyDraft] = useState<Record<string, string>>({});
  const [priorityDraft, setPriorityDraft] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<'open' | 'active' | 'all' | 'resolved'>('open');
  const [alertBody, setAlertBody] = useState('');
  const [alertKind, setAlertKind] = useState<'orientation_technique' | 'information'>('information');
  const [alertRows, setAlertRows] = useState<AlertRow[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const [messageRes, alertRes] = await Promise.all([
      kuunda
        .from('messages')
        .select('id, subject, body, status, priority, created_at, sender_id')
        .eq('is_preoccupation', true)
        .order('created_at', { ascending: false }),
      profile && isAdmin
        ? kuunda
            .from('notifications')
            .select('id, body, kind, status, created_at')
            .eq('sender_id', profile.id)
            .in('kind', ['information', 'orientation_technique'])
            .order('created_at', { ascending: false })
        : Promise.resolve({ data: [], error: null }),
    ]);
    if (messageRes.error) {
      toast.error(messageRes.error.message);
      setLoading(false);
      return;
    }
    if (alertRes.error) {
      toast.error(alertRes.error.message);
    }
    const rows = messageRes.data ?? [];
    const senderIds = [...new Set(rows.map((r) => r.sender_id))];
    const { data: profiles } = await kuunda.from('profiles').select('id, email').in('id', senderIds);
    const emailById = Object.fromEntries((profiles ?? []).map((p) => [p.id, p.email]));
    setItems(rows.map((r) => ({ ...r, sender_email: emailById[r.sender_id] })));
    setAlertRows((alertRes.data ?? []) as AlertRow[]);
    const priorities: Record<string, string> = {};
    for (const r of rows) priorities[r.id] = r.priority;
    setPriorityDraft(priorities);
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, []);

  const visible = useMemo(() => {
    if (filter === 'all') return items;
    if (filter === 'open') return items.filter((i) => i.status === 'open');
    if (filter === 'active') return items.filter((i) => i.status === 'open' || i.status === 'in_progress');
    return items.filter((i) => i.status === 'resolved' || i.status === 'closed');
  }, [items, filter]);

  async function sendReply(row: PreoccRow) {
    if (!profile) return;
    const body = (replyDraft[row.id] ?? '').trim();
    if (body.length < 5) {
      toast.error('Réponse trop courte.');
      return;
    }
    const { error: msgErr } = await kuunda.from('messages').insert({
      sender_id: profile.id,
      receiver_id: row.sender_id,
      body,
      is_preoccupation: false,
      status: 'sent',
    });
    if (msgErr) {
      toast.error(msgErr.message);
      return;
    }
    const { error: notifErr } = await kuunda.from('notifications').insert({
      user_id: row.sender_id,
      sender_id: profile.id,
      message_id: row.id,
      subject: row.subject ? `Re: ${row.subject}` : 'Réponse à votre préoccupation',
      body,
      kind: 'preoccupation_reply',
      priority: row.priority,
      status: 'unread',
    });
    if (notifErr) {
      toast.error(notifErr.message);
      return;
    }
    await kuunda.from('messages').update({ status: 'in_progress' }).eq('id', row.id);
    setReplyDraft({ ...replyDraft, [row.id]: '' });
    toast.success('Réponse envoyée.', 'Assistance');
    await load();
  }

  async function closeConcern(id: string) {
    const { error: err } = await kuunda.from('messages').update({ status: 'resolved' }).eq('id', id);
    if (err) {
      toast.error(err.message);
    } else {
      toast.success('Préoccupation clôturée.', 'Assistance');
      await load();
    }
  }

  async function reopenConcern(id: string) {
    const { error: err } = await kuunda.from('messages').update({ status: 'open' }).eq('id', id);
    if (err) {
      toast.error(err.message);
    } else {
      toast.info('Préoccupation rouverte.', 'Assistance');
      await load();
    }
  }

  async function savePriority(id: string) {
    if (!isAdmin && !isStaff) return;
    const priority = priorityDraft[id] ?? 'medium';
    const { error: err } = await kuunda.from('messages').update({ priority }).eq('id', id);
    if (err) {
      toast.error(err.message);
    } else {
      toast.success('Priorité mise à jour.', 'Assistance');
      await load();
    }
  }

  async function broadcastAlert() {
    if (!profile || !isAdmin) return;
    const text = alertBody.trim();
    if (text.length < 10) {
      toast.error('Message d’alerte trop court.');
      return;
    }
    const { data: users } = await kuunda.from('profiles').select('id').eq('role', 'user').eq('is_active', true);
    const rows = (users ?? []).map((u) => ({
      user_id: u.id,
      sender_id: profile.id,
      subject: alertKind === 'orientation_technique' ? 'Alerte ISBD' : 'Information ISBD',
      body: text,
      kind: alertKind,
      priority: 'high',
      status: 'unread',
      is_read: false,
    }));
    if (rows.length === 0) {
      toast.error('Aucun utilisateur à notifier.');
      return;
    }
    const { error: err } = await kuunda.from('notifications').insert(rows);
    if (err) {
      toast.error(err.message);
    } else {
      toast.success(`Alerte diffusée à ${rows.length} utilisateur(s).`, 'Alerte');
      setAlertBody('');
    }
  }

  const alertSummaries = useMemo(() => {
    const grouped = new Map<
      string,
      { ids: string[]; kind: string; body: string; created_at: string; total: number; unread: number }
    >();
    for (const row of alertRows) {
      const bucket = new Date(row.created_at).toISOString().slice(0, 16);
      const key = `${row.kind}|${row.body}|${bucket}`;
      const existing = grouped.get(key);
      if (existing) {
        existing.ids.push(row.id);
        existing.total += 1;
        if (row.status === 'unread') existing.unread += 1;
      } else {
        grouped.set(key, {
          ids: [row.id],
          kind: row.kind,
          body: row.body,
          created_at: row.created_at,
          total: 1,
          unread: row.status === 'unread' ? 1 : 0,
        });
      }
    }
    return [...grouped.values()].sort((a, b) => b.created_at.localeCompare(a.created_at));
  }, [alertRows]);

  async function deleteAlertSummary(ids: string[]) {
    if (!isAdmin || ids.length === 0) return;
    if (!window.confirm('Supprimer cet élément de synthèse ?')) return;
    const { error: err } = await kuunda.from('notifications').delete().in('id', ids);
    if (err) toast.error(err.message);
    else {
      toast.success('Élément supprimé de la synthèse des alertes.', 'Alerte');
      await load();
    }
  }

  return (
    <div className="admin-page">
      <AdminPageHeader
        title="Assistance"
        description="Préoccupations des entrepreneurs et diffusion d’alertes."
        actions={
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => void load()}>
            Actualiser
          </button>
        }
      />

      {isAdmin ? (
        <section className="admin-panel">
          <div className="admin-panel-title">
            <h3>Diffuser une alerte</h3>
          </div>
          <div className="admin-toolbar">
            <div className="field">
              <label>Type</label>
              <select
                value={alertKind}
                onChange={(e) => setAlertKind(e.target.value as 'orientation_technique' | 'information')}
              >
                <option value="information">Information</option>
                <option value="orientation_technique">Orientation technique</option>
              </select>
            </div>
          </div>
          <div className="field">
            <label>Message</label>
            <textarea value={alertBody} onChange={(e) => setAlertBody(e.target.value)} />
          </div>
          <button type="button" className="btn btn-accent" onClick={() => void broadcastAlert()}>
            Diffuser
          </button>
        </section>
      ) : null}

      {isAdmin ? (
        <section className="admin-panel">
          <div className="admin-panel-title">
            <h3>Synthèse des alertes diffusées</h3>
          </div>
          {alertSummaries.length === 0 ? (
            <p className="muted">Aucune alerte diffusée pour le moment.</p>
          ) : (
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {alertSummaries.map((alert) => (
                <article key={alert.ids.join(',')} className="admin-list-item">
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <strong>{alert.kind === 'information' ? 'Information' : 'Orientation technique'}</strong>
                    <span className="muted">{new Date(alert.created_at).toLocaleString('fr-FR')}</span>
                  </div>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{alert.body}</p>
                  <div className="muted" style={{ fontSize: '0.9rem' }}>
                    Diffusée à {alert.total} utilisateur(s) · {alert.unread} non lue(s)
                  </div>
                  <div className="admin-row-actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => void deleteAlertSummary(alert.ids)}
                    >
                      Supprimer de la synthèse
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      ) : null}

      <section className="admin-panel">
        <div className="admin-filters" style={{ marginBottom: '1rem' }}>
          <button type="button" className={`admin-filter${filter === 'open' ? ' is-active' : ''}`} onClick={() => setFilter('open')}>
            Ouvertes ({items.filter((i) => i.status === 'open').length})
          </button>
          <button
            type="button"
            className={`admin-filter${filter === 'active' ? ' is-active' : ''}`}
            onClick={() => setFilter('active')}
          >
            Actives
          </button>
          <button
            type="button"
            className={`admin-filter${filter === 'resolved' ? ' is-active' : ''}`}
            onClick={() => setFilter('resolved')}
          >
            Clôturées
          </button>
          <button type="button" className={`admin-filter${filter === 'all' ? ' is-active' : ''}`} onClick={() => setFilter('all')}>
            Toutes ({items.length})
          </button>
        </div>

        {loading ? <ListSkeleton rows={4} /> : null}
        {!loading && visible.length === 0 ? (
          <AdminEmpty icon="lifebuoy" title="Aucune préoccupation" hint="Rien à traiter pour ce filtre." />
        ) : null}

        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {visible.map((m) => (
            <article key={m.id} className="admin-list-item">
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                <strong>{m.sender_email ?? m.sender_id}</strong>
                <span className="muted">{new Date(m.created_at).toLocaleString('fr-FR')}</span>
              </div>
              {m.subject ? <h4 style={{ margin: '0.35rem 0 0.25rem' }}>{m.subject}</h4> : null}
              <p>{m.body}</p>
              <p>
                Statut :{' '}
                <span className={`chip ${m.status === 'resolved' || m.status === 'closed' ? 'ok' : 'warn'}`}>
                  {preoccupationStatusLabel(m.status)}
                </span>
              </p>
              <div className="grid-2">
                <div className="field">
                  <label>Priorité</label>
                  <select
                    value={priorityDraft[m.id] ?? m.priority}
                    onChange={(e) => setPriorityDraft({ ...priorityDraft, [m.id]: e.target.value })}
                  >
                    <option value="low">Faible</option>
                    <option value="medium">Moyenne</option>
                    <option value="high">Importante</option>
                    <option value="critical">Critique</option>
                  </select>
                </div>
                <div className="admin-row-actions" style={{ alignItems: 'end' }}>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => void savePriority(m.id)}>
                    Sauver priorité
                  </button>
                  {m.status === 'resolved' || m.status === 'closed' ? (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => void reopenConcern(m.id)}>
                      Rouvrir
                    </button>
                  ) : (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => void closeConcern(m.id)}>
                      Clôturer
                    </button>
                  )}
                </div>
              </div>
              {m.status !== 'resolved' && m.status !== 'closed' ? (
                <>
                  <div className="field">
                    <label>Réponse</label>
                    <textarea
                      value={replyDraft[m.id] ?? ''}
                      onChange={(e) => setReplyDraft({ ...replyDraft, [m.id]: e.target.value })}
                      placeholder="Écrire la réponse au porteur…"
                    />
                  </div>
                  <button type="button" className="btn btn-primary" onClick={() => void sendReply(m)}>
                    Envoyer la réponse
                  </button>
                </>
              ) : null}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
